import { translateBackend } from '@assets/i18n/config';
import type { AlertBanner } from '@generated/graphql';
import CloseIcon from '@mui/icons-material/Close';
import type { SxProps } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { BANNER_TYPE_OPTIONS } from '../constants';

const AlertBannerDisplay = ({
  bannerFields,
  onDismiss,
  onAction,
  sx,
}: {
  bannerFields: Partial<AlertBanner>;
  onDismiss?: () => void;
  onAction?: () => void;
  sx?: SxProps;
}) => {
  const { sys, ref } = useThemeTokens();

  const isDismissible = bannerFields.configuration?.makeDismissible ?? false;
  const hasAction = bannerFields.configuration?.enableAction ?? false;
  const actionLabel = bannerFields.actionButton?.label || '';

  const config = BANNER_TYPE_OPTIONS.find(
    (option) => option.key === bannerFields.bannerType,
  );

  if (!config) return null;

  const iconColor = ref.palette[config.iconColor];

  return (
    <Box
      display='flex'
      alignItems='center'
      flex={1}
      p={1}
      bgcolor={ref.palette[config.backgroundColor]}
      borderRadius={sys.borderRadius.sm}
      sx={sx}
    >
      <Box
        flex={1}
        display='flex'
        justifyContent='center'
        alignItems='center'
        gap={1}
      >
        <Icon icon={config.icon} sx={{ color: iconColor }} />

        <Typography variant='bodyMedium'>
          {translateBackend(bannerFields.bannerCopy)}
        </Typography>
      </Box>

      {(hasAction || isDismissible) && (
        <Box display='flex' alignItems='center' gap={1}>
          {hasAction && actionLabel && (
            <Button
              variant='outlined'
              size='sm'
              label={actionLabel}
              onClick={onAction}
              sx={{
                borderColor: `${iconColor} !important`,
                '&:hover': {
                  borderColor: `${iconColor} !important`,
                  backgroundColor: `${iconColor}14 !important`,
                },
              }}
            />
          )}

          {isDismissible && (
            <IconButton
              size='small'
              onClick={onDismiss}
              sx={{ color: iconColor }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AlertBannerDisplay;
